import { useCallback, useEffect, useMemo } from 'react'
import { Separator } from '../../../components/separator'
import { useAppSelector } from '../../../redux/hook'
import { solicitorTypeItems } from '../../../utils/shared-dropdown';
import { ApplicationStep3 } from '../../../models/classes/ApplicationStep3.class';
import { SolicitorTypeEnum } from '../../../models/enum/solicitor-type.enum';
import { LoanTypeCode } from '../../../utils/code-converter';
import SolicitorTableMobile, { MobileTableItemInterface } from '../../../components/mobile/mobile-table';
import useIsMobile from '../../../hooks/use-is-mobile';
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum';
import { convertRoleToStructureStatus } from '../../../utils/form-helper';
import { MyInfoWithDOD } from '../../../models/polymer/MyInfo.model';

interface SolicitorTableProps{
  ConveyancingValue: ApplicationStep3;
}

type solicitorType = "current" | "proposed";
function SolicitorTable({...props}:SolicitorTableProps) {
  const { ConveyancingValue } = props;
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const { isMobile } = useIsMobile();
  const loginUserStructureStatus = useAppSelector(state => state.auth.AuthenticationSlice.structureStatus);
  const isMainApplicantOutgoing = loginUserStructureStatus === StructureStatusEnum.outgoing;

  const hasExecutorAdministrator = useMemo(()=>{
    return applicationStartFormData.selected_hdb_property?.additional_roles.some(e=>{
      const roleStructureStatus = convertRoleToStructureStatus(e.role_name);
      return roleStructureStatus === StructureStatusEnum.executor || roleStructureStatus === StructureStatusEnum.administrator
    })
  },[applicationStartFormData.selected_hdb_property?.additional_roles])

  const executorAdministratorListing:MyInfoWithDOD[] = useMemo(()=>{
    let dataList:MyInfoWithDOD[] = [];
    if(hasExecutorAdministrator){
      applicationStartFormData.selected_hdb_property?.additional_roles.forEach(e=>{
        const roleStructureStatus = convertRoleToStructureStatus(e.role_name);
        if(roleStructureStatus === StructureStatusEnum.executor || roleStructureStatus === StructureStatusEnum.administrator){
          dataList.push(e);
        }
      })
    }
    return dataList;
  },[applicationStartFormData.selected_hdb_property?.additional_roles, hasExecutorAdministrator])

  const ConveyancingRowUnderSolicitorColumn = useCallback((type: solicitorType)=>{
    if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.conveyancing_hdb_solicitor) {
      return <p>HDB (Conveyancing)</p>
    }
    else if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm){
      if(ConveyancingValue[type].hdb_solicitor.represented_same_solicitor) {
        return <p>HDB (Conveyancing)</p>
      } else if(ConveyancingValue[type].conveyancing_private_solicitor.is_confirm){
        return <p>{ConveyancingValue[type].conveyancing_private_solicitor.name_of_firm}(Conveyancing)</p>
      }
    }
    else if((ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.conveyancing_private_solicitor || ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.private_solicitor)){
      if(ConveyancingValue[type].conveyancing_private_solicitor.is_confirm) {
        return <p>{ConveyancingValue[type].conveyancing_private_solicitor.name_of_firm} {`(Conveyancing)`}</p>
      } else if(ConveyancingValue[type].is_conveyancing_hdb && ConveyancingValue[type].hdb_solicitor.is_confirm){
        return <p>HDB (Conveyancing)</p>
      }
    }
  },[ConveyancingValue])

  const SolicitorColumn = useCallback((type: solicitorType) =>{

    const selection_type = type === "current" ? ConveyancingValue.current_solicitor_type : ConveyancingValue.proposed_solicitor_type;
    const postfix_title = type === "current" ? "(Discharge of current loan)" : "(New mortgage loan)"

    if(selection_type && ((selection_type === SolicitorTypeEnum.hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm) || (selection_type === SolicitorTypeEnum.private_solicitor && ConveyancingValue[type].private_solicitor.is_confirm))){
      
      const solicitor_type = selection_type === SolicitorTypeEnum.hdb_solicitor ? ConveyancingValue[type].hdb_solicitor : ConveyancingValue[type].private_solicitor;
      const sameRepresentation = solicitor_type.represented_same_solicitor;

      const primary_selection = selection_type === SolicitorTypeEnum.hdb_solicitor ? solicitorTypeItems.find(e => e.value === selection_type)?.title : ConveyancingValue[type].private_solicitor.name_of_firm;

      if(sameRepresentation){
        return <div>
            <p>{primary_selection}</p>
          </div>
      }
      else{
        return <>
          <p>{primary_selection} {postfix_title}</p>
          {ConveyancingRowUnderSolicitorColumn(type)}
        </>
      }
    }
    else if(selection_type && ((selection_type === SolicitorTypeEnum.conveyancing_hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm) || (selection_type === SolicitorTypeEnum.conveyancing_private_solicitor && ConveyancingValue[type].conveyancing_private_solicitor.is_confirm))){
      return <>
        {ConveyancingRowUnderSolicitorColumn(type)}
      </>
    }
    else{
      if(type === "proposed" && isMainApplicantOutgoing){
        return <p className='ohc-household-structure-table-placeholder'>Pending proposed owner(s)</p>
      }
      return <p className='ohc-household-structure-table-placeholder'>You have not selected any lawyer to represent the {type} owners</p>
    }
    
  },[ConveyancingRowUnderSolicitorColumn, ConveyancingValue, isMainApplicantOutgoing]);



  useEffect(()=>{
    console.log("DEBUG SOLICITOR - applicationStep2FormData",applicationStep2FormData);
  },[applicationStep2FormData])


  const ConveyancingRowUnderSolicitorMobileColumn = useCallback((type: solicitorType)=>{
    if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.conveyancing_private_solicitor && ConveyancingValue[type].conveyancing_private_solicitor.is_confirm){
      return `${ConveyancingValue[type].conveyancing_private_solicitor.name_of_firm} (Conveyancing)`;
    }else if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.conveyancing_hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm){
      return 'HDB (Conveyancing)'
    }else if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.private_solicitor) {
      if(ConveyancingValue[type].conveyancing_private_solicitor.is_confirm) {
        return `${ConveyancingValue[type].conveyancing_private_solicitor.name_of_firm} (Conveyancing)`;
      } else if (ConveyancingValue[type].is_conveyancing_hdb && ConveyancingValue[type].hdb_solicitor.is_confirm) {
        return 'HDB (Conveyancing)'
      } else {
        return '';
      }
    }else if(ConveyancingValue[`${type}_solicitor_type`] === SolicitorTypeEnum.hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm) {
      if(ConveyancingValue[type].hdb_solicitor.represented_same_solicitor) {
        return 'HDB (Conveyancing)'
      }else if(ConveyancingValue[type].conveyancing_private_solicitor.is_confirm){
         return `${ConveyancingValue[type].conveyancing_private_solicitor.name_of_firm} (Conveyancing)`;
      } else {
        return '';
      }
    }
    else{
      return '';
    }
  },[ConveyancingValue])

  const SolicitorMobileColumn = useCallback((type: solicitorType) => {

    const selection_type = type === "current" ? ConveyancingValue.current_solicitor_type : ConveyancingValue.proposed_solicitor_type;
    const postfix_title = type === "current" ? "(Discharge of current loan)" : "(New mortgage loan)"

    if(selection_type && ((selection_type === SolicitorTypeEnum.hdb_solicitor && ConveyancingValue[type].hdb_solicitor.is_confirm) || (selection_type === SolicitorTypeEnum.private_solicitor && ConveyancingValue[type].private_solicitor.is_confirm))){
      
      const solicitor_type = selection_type === SolicitorTypeEnum.hdb_solicitor ? ConveyancingValue[type].hdb_solicitor : ConveyancingValue[type].private_solicitor;
      const sameRepresentation = solicitor_type.represented_same_solicitor;

      const primary_selection = selection_type === SolicitorTypeEnum.hdb_solicitor ? solicitorTypeItems.find(e => e.value === selection_type)?.title ?? "" : ConveyancingValue[type].private_solicitor.name_of_firm;
      if(sameRepresentation){
        return [primary_selection];
      }
      else{
        return [`${primary_selection} ${postfix_title}`, ConveyancingRowUnderSolicitorMobileColumn(type)];
      }
     
    }
    else{
      return [ConveyancingRowUnderSolicitorMobileColumn(type)]; 
    }
    
  },[ConveyancingRowUnderSolicitorMobileColumn, ConveyancingValue]);



  const current_mobile_table_items = useMemo(()=>{
    let obj_items:MobileTableItemInterface[] = [
      { header:"Owners", contents: applicationStep1FormData.household?.owners.map(e=>e.myinfo.name), placeholder:"" },
      { header:"Loan", contents: [ LoanTypeCode.find(e => e.code === applicationStep2FormData.current_loan_type)?.description ?? ""],  placeholder:"You have not selected any loan type for current owners" },
      { header:"Lawyer", contents: [...SolicitorMobileColumn("current")],  placeholder:"You have not selected any lawyer to represent the current owners" },

    ];
    if(hasExecutorAdministrator && applicationStartFormData.selected_hdb_property?.additional_roles){
      obj_items.push({ header:"Executor/Administrator", contents: applicationStartFormData.selected_hdb_property?.additional_roles.map(additional_role => `${additional_role.name} (${additional_role.role_name})`),  placeholder:"" });
    }
    return obj_items;
  },[SolicitorMobileColumn, applicationStep1FormData.household?.owners, applicationStep2FormData.current_loan_type, hasExecutorAdministrator, applicationStartFormData.selected_hdb_property?.additional_roles])

 
  const proposed_mobile_table_items = useMemo(()=>{
    let obj_items:MobileTableItemInterface[] = [
      { header:"Owners", contents: applicationStep1FormData.household?.incoming_owners.map(e=>e.myinfo.name), placeholder:"" },
      { header:"Loan", contents: [ LoanTypeCode.find(e => e.code === applicationStep2FormData.new_loan_type)?.description ?? "" ], placeholder: isMainApplicantOutgoing ? "Pending proposed owner(s)" : "You have not selected any loan type for proposed owners" },
      { header:"Lawyer", contents: [...SolicitorMobileColumn("proposed")],  placeholder: isMainApplicantOutgoing ? "Pending proposed owner(s)" : "You have not selected any lawyer to represent the proposed owners" },

    ];
    if(hasExecutorAdministrator && applicationStartFormData.selected_hdb_property?.additional_roles){
      obj_items.push({ header:"Executor/Administrator", contents: applicationStartFormData.selected_hdb_property?.additional_roles.map(additional_role => `${additional_role.name} (${additional_role.role_name})`),  placeholder:"" });
    }
    return obj_items;
  },[SolicitorMobileColumn, applicationStep1FormData.household?.owners, applicationStep2FormData.new_loan_type, isMainApplicantOutgoing, hasExecutorAdministrator, applicationStartFormData.selected_hdb_property?.additional_roles])



  if(isMobile){
    return <>
      <SolicitorTableMobile title='Current household' items={current_mobile_table_items}/>
      <div style={{height:20}}></div>
      <SolicitorTableMobile title='Proposed household' items={proposed_mobile_table_items}/>
    </>
  }
 
  return (
    <div>
        <div className='ohc-household-structure-table'>
          <div className='ohc-household-structure-table-header'>
            <div className='ohc-household-structure-table-column-1'></div>
            <div className='ohc-household-structure-table-column-2'>
              <p>Current household</p>
            </div>
            <div className='ohc-household-structure-table-column-2'>
              <p>Proposed household</p>
            </div>
          </div>
          <Separator style={{ margin: "16px 0"}} variant="solid"/>
          <div className='ohc-household-structure-table-content'>
            <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header'>
              <p>Owners</p>
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {
                applicationStep1FormData.household?.owners.length > 0 
                ?(
                  applicationStep1FormData.household?.owners?.map((owner, index)=> {
                    const key = index;
                    return <p key={key}>{owner?.myinfo?.name}</p>
                  })
                )
                : (
                  <p>-</p>
                )
              }
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {
                applicationStep1FormData.household.incoming_owners?.map((owner, index) => {
                  const key = index;
                  return <p key={key}>{owner.myinfo?.name}</p>
                })
              }
            </div>

          </div>
          {hasExecutorAdministrator && <>
            <Separator style={{margin:"16px 0"}}/>
            <div className='ohc-household-structure-table-content'>
              <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header' style={{display: "block"}}>
                <p>Executor/</p>
                <p>Administrator</p>
              </div>
              <div className='ohc-household-structure-table-column-2'>
                { executorAdministratorListing.map((additional_role,__index)=>{
                    const key = "additional_role"+__index;
                    return <p key={key}>{additional_role.name} ({additional_role.role_name})</p>
                  })
                }
              </div>
            </div>
          </>}
          <Separator style={{ margin: "16px 0" }} />
          <div className='ohc-household-structure-table-content'>
            <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header'>
              <p>Loan</p>
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {applicationStep2FormData.current_loan_type && <p>{LoanTypeCode.find(e => e.code === applicationStep2FormData.current_loan_type)?.description ?? ""}</p>}
              {!applicationStep2FormData.current_loan_type && <p className='ohc-household-structure-table-placeholder'>You have not selected any loan type for current owners</p>}
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {applicationStep2FormData.new_loan_type && <p>{LoanTypeCode.find(e => e.code === applicationStep2FormData.new_loan_type)?.description ?? ""}</p>}
              {!applicationStep2FormData.new_loan_type && <p className='ohc-household-structure-table-placeholder'>{isMainApplicantOutgoing ? "Pending proposed owner(s)" : "You have not selected any loan type for proposed owners" }</p>}
            </div>

          </div>
          <Separator style={{ margin: "16px 0" }} />
          <div className='ohc-household-structure-table-content'>
            <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header'>
              <p>Lawyer</p>
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {SolicitorColumn("current")}
            </div>

            <div className='ohc-household-structure-table-column-2'>
              {SolicitorColumn("proposed")}
            </div>

          </div>
        </div>
      </div>
  )
}

export default SolicitorTable